import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { usePagedData } from "@/hooks/usePagedData";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { Pager, SearchInput } from "@/components/common/Pager";
import { MigoPanel } from "@/pages/MigoPanel";
import { CockpitPanel } from "@/pages/CockpitPanel";
import { eur, num, shortDate } from "@/lib/format";
import { ClipboardList, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ACCENT = "#0D9488";

function InfoRecordDialog({ open, onOpenChange, onSaved }) {
  const { items: vendors } = usePagedData("/vendors", 200);
  const { items: materials } = usePagedData("/materials", 200);
  const [f, setF] = useState({ vendor_code: "", material_code: "", plant: "UK", net_price: "", min_qty: 250, planned_delivery_days: 7 });
  const submit = async () => {
    const v = vendors.find((x) => x.vendor_code === f.vendor_code);
    const m = materials.find((x) => x.material_code === f.material_code);
    try {
      await api.post("/info-records", { ...f, vendor_name: v?.name || f.vendor_code, material_name: m?.name || f.material_code, net_price: Number(f.net_price), min_qty: Number(f.min_qty), planned_delivery_days: Number(f.planned_delivery_days) });
      toast.success("Info record created"); onOpenChange(false); onSaved();
      setF({ vendor_code: "", material_code: "", plant: "UK", net_price: "", min_qty: 250, planned_delivery_days: 7 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create info record"); }
  };
  const inp = "rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[#e5e9f0] bg-white text-[#32363a]">
        <DialogHeader><DialogTitle className="uppercase">Create Info Record · EP-PU04 · S/4 ME11</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <select data-testid="ir-vendor" value={f.vendor_code} onChange={(e) => setF({ ...f, vendor_code: e.target.value })} className={inp}><option value="">Select vendor…</option>{vendors.map((v) => <option key={v.vendor_code} value={v.vendor_code}>{v.name}</option>)}</select>
          <select data-testid="ir-material" value={f.material_code} onChange={(e) => setF({ ...f, material_code: e.target.value })} className={inp}><option value="">Select material…</option>{materials.map((m) => <option key={m.material_code} value={m.material_code}>{m.name}</option>)}</select>
          <input data-testid="ir-price" type="number" step="0.01" placeholder="Net price (£)" value={f.net_price} onChange={(e) => setF({ ...f, net_price: e.target.value })} className={inp} />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" placeholder="Min qty" value={f.min_qty} onChange={(e) => setF({ ...f, min_qty: e.target.value })} className={inp} />
            <input type="number" placeholder="Planned deliv. days" value={f.planned_delivery_days} onChange={(e) => setF({ ...f, planned_delivery_days: e.target.value })} className={inp} />
          </div>
        </div>
        <DialogFooter><button data-testid="ir-submit" onClick={submit} disabled={!f.vendor_code || !f.material_code || !f.net_price} className="rounded-lg bg-[#0D9488] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Create</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Purchasing() {
  const navigate = useNavigate();
  const prs = usePagedData("/purchase-requisitions");
  const vendors = usePagedData("/vendors");
  const irs = usePagedData("/info-records");
  const [irOpen, setIrOpen] = useState(false);

  const prCols = [
    { key: "pr_no", label: "Requisition", render: (r) => <span className="font-mono text-xs text-[#0D9488]">{r.pr_no}</span> },
    { key: "material_name", label: "Material" },
    { key: "requester", label: "Requester" },
    { key: "vendor_name", label: "Source Vendor", render: (r) => r.vendor_name || <span className="text-[#6a6d70]">—</span> },
    { key: "net_value", label: "Est. Value", render: (r) => <span className="font-mono">{eur(r.net_value)}</span> },
    { key: "po_ref", label: "PO", render: (r) => r.po_ref ? <span className="font-mono text-[10px] text-[#F97316]">{r.po_ref}</span> : <span className="text-[#6a6d70]">—</span> },
    { key: "status", label: "Status", badge: true },
  ];
  const venCols = [
    { key: "vendor_code", label: "Vendor", render: (r) => <span className="font-mono text-xs text-[#0D9488]">{r.vendor_code}</span> },
    { key: "name", label: "Name" },
    { key: "city", label: "City" },
    { key: "country", label: "Country" },
    { key: "spend_tytd", label: "Spend YTD", render: (r) => <span className="font-mono">{eur(r.spend_tytd)}</span> },
    { key: "currency", label: "Curr." },
  ];
  const irCols = [
    { key: "info_no", label: "Info Rec.", render: (r) => <span className="font-mono text-xs text-[#0D9488]">{r.info_no}</span> },
    { key: "material_name", label: "Material" },
    { key: "vendor_name", label: "Vendor" },
    { key: "net_price", label: "Net Price", render: (r) => <span className="font-mono">{eur(r.net_price)}</span> },
    { key: "min_qty", label: "Min Qty", render: (r) => <span className="font-mono">{num(r.min_qty)}</span> },
    { key: "planned_delivery_days", label: "Lead (days)", render: (r) => <span className="font-mono">{r.planned_delivery_days}</span> },
    { key: "created_at", label: "Since", render: (r) => shortDate(r.created_at) },
  ];

  return (
    <div>
      <PageHeader title="Purchasing" subtitle="Procure-to-pay: purchase requisitions (EP-PU01), supplier master (EP-PU03), info records (EP-PU04) and goods movements (EP-MM10)." icon={ClipboardList} accent={ACCENT} />

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Open Requisitions" value={(prs.stats.open || 0).toLocaleString()} accent={ACCENT} />
        <KpiCard label="Requisition Value" value={eur(prs.stats.net_value || 0)} accent={ACCENT} />
        <KpiCard label="Active Vendors" value={vendors.total.toLocaleString()} accent="#0284C7" />
        <KpiCard label="Info Records" value={irs.total.toLocaleString()} accent="#F97316" />
      </div>

      <Tabs defaultValue="cockpit">
        <TabsList className="bg-white border border-[#e5e9f0] flex-wrap h-auto">
          <TabsTrigger data-testid="tab-cockpit" value="cockpit" className="data-[state=active]:bg-[#e6f4f2] data-[state=active]:text-[#0D9488]">Cockpit</TabsTrigger>
          <TabsTrigger data-testid="tab-req" value="req" className="data-[state=active]:bg-[#e6f4f2] data-[state=active]:text-[#0D9488]">Requisitions</TabsTrigger>
          <TabsTrigger data-testid="tab-vendors" value="vendors" className="data-[state=active]:bg-[#e6f4f2] data-[state=active]:text-[#0D9488]">Vendors</TabsTrigger>
          <TabsTrigger data-testid="tab-info" value="info" className="data-[state=active]:bg-[#e6f4f2] data-[state=active]:text-[#0D9488]">Info Records</TabsTrigger>
          <TabsTrigger data-testid="tab-migo" value="migo" className="data-[state=active]:bg-[#e6f4f2] data-[state=active]:text-[#0D9488]">Goods Movement</TabsTrigger>
        </TabsList>

        <TabsContent value="cockpit" className="mt-4"><CockpitPanel /></TabsContent>

        <TabsContent value="req" className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <SearchInput testId="pr-search" value={prs.q} onChange={prs.setQ} placeholder="Search requisitions by number, requester, material…" />
            <button data-testid="new-pr-btn" onClick={() => navigate("/requisitions/new")} className="inline-flex items-center gap-2 rounded-lg bg-[#0D9488] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0b7d73]"><Plus size={16} /> New Requisition (EP-PU01)</button>
          </div>
          {prs.loading && prs.items.length === 0 ? <Loader /> : <DataTable testId="pr-table" columns={prCols} rows={prs.items} onRowClick={(r) => navigate(`/requisitions/${r.id}`)} />}
          <Pager testId="pr-pager" page={prs.page} pages={prs.pages} total={prs.total} pageSize={prs.pageSize} onPage={prs.setPage} />
        </TabsContent>

        <TabsContent value="vendors" className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <SearchInput testId="vendor-search" value={vendors.q} onChange={vendors.setQ} placeholder="Search vendors by code, name, city, country…" />
            <button data-testid="new-vendor-btn" onClick={() => navigate("/vendors/new")} className="inline-flex items-center gap-2 rounded-lg bg-[#0D9488] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0b7d73]"><Users size={16} /> New Vendor (EP-PU03)</button>
          </div>
          {vendors.loading && vendors.items.length === 0 ? <Loader /> : <DataTable testId="vendor-table" columns={venCols} rows={vendors.items} onRowClick={(r) => navigate(`/vendors/${encodeURIComponent(r.vendor_code)}`)} />}
          <Pager testId="vendor-pager" page={vendors.page} pages={vendors.pages} total={vendors.total} pageSize={vendors.pageSize} onPage={vendors.setPage} />
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <SearchInput testId="ir-search" value={irs.q} onChange={irs.setQ} placeholder="Search info records by material or vendor…" />
            <button data-testid="new-ir-btn" onClick={() => setIrOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-[#0D9488] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0b7d73]"><Plus size={16} /> New Info Record (EP-PU04)</button>
          </div>
          {irs.loading && irs.items.length === 0 ? <Loader /> : <DataTable testId="ir-table" columns={irCols} rows={irs.items} />}
          <Pager testId="ir-pager" page={irs.page} pages={irs.pages} total={irs.total} pageSize={irs.pageSize} onPage={irs.setPage} />
        </TabsContent>

        <TabsContent value="migo" className="mt-4"><MigoPanel /></TabsContent>
      </Tabs>

      <InfoRecordDialog open={irOpen} onOpenChange={setIrOpen} onSaved={irs.reload} />
    </div>
  );
}
